import { Button, IconButton, Tooltip, useTranslationContext } from 'platform/components';
import { HStack } from 'platform/foundation-tw';
import { match } from 'ts-pattern';
import { Nullish, suffixTestId, TestIdProps } from 'shared/web';
import { useEagGridConfig } from '../../context/useEagGridConfig';
import { useIsMobileView } from '../../hooks/useIsMobileView';
import { usePresetState } from '../../hooks/usePresetState';
import { useSmartSearchEnabled, usePresetsEnabled, useGlobalActionsEnabled, useFilterToolPanelVisible, useColumnToolPanelVisible, useSettingsToolPanelVisible } from '../../store/selectors';
import { FeGridSettings, GetDataQueryResponse, TransformedDefinitionApiResponse } from '../../types/Api';
import { EagColumnState } from '../../types/EagColumnState';
import { EagGridProps } from '../../types/EagGridProps';
import { ToolpanelType } from '../../types/ToolpanelType';
import { getPresetStates } from '../../utils/getPresetStates';
import { isPresetCustom } from '../../utils/isPresetCustom';
import { refreshDatagrid } from '../../utils/refreshDatagrid';
import { PresetsPanelRenderer } from '../PresetPanel/PresetPanel';
import { ExportButton } from './components/ExportButton/ExportButton';
import { LoadingBar } from './components/LoadingBar/LoadingBar';
import { MobilePresetsButton } from './components/MobilePresetsButton/MobilePresetsButton';
import { QuickFilterInput } from './components/QuickFilterInput/QuickFilterInput';
import { SharePresetButton } from './components/SharePresetButton/SharePresetButton';
export type GridHeaderProps = {
  dataGridSettings: FeGridSettings;
  dataGridProps: EagGridProps;
  definition: TransformedDefinitionApiResponse;
  dataQuery: GetDataQueryResponse | Nullish;
  refreshData: () => void;
  columnStates: EagColumnState[];
  activePanel: ToolpanelType | Nullish;
  onTogglePanel: (panel: ToolpanelType) => void;
  isFilterActive: boolean;
  onResetFilters: () => void;
} & TestIdProps;
const getPanelPriority = (activePanel: ToolpanelType | Nullish, panel: ToolpanelType) => match(activePanel === panel).with(true, () => 'secondary' as const).otherwise(() => undefined);
const getFilterPriority = (activePanel: ToolpanelType | Nullish, isFilterActive: boolean) => match({
  isOpen: activePanel === ToolpanelType.filter,
  isFilterActive
}).with({
  isFilterActive: true
}, () => 'primary' as const).with({
  isOpen: true
}, () => 'secondary' as const).otherwise(() => undefined);
export function GridHeader(props: GridHeaderProps) {
  const t = useTranslationContext();
  const {
    buildAbsoluteUrl
  } = useEagGridConfig();
  const isMobile = useIsMobileView();
  const smartSearchEnabled = useSmartSearchEnabled();
  const presetsEnabled = usePresetsEnabled();
  const globalActionsEnabled = useGlobalActionsEnabled();
  const filterToolPanelVisible = useFilterToolPanelVisible();
  const columnToolPanelVisible = useColumnToolPanelVisible();
  const settingsToolPanelVisible = useSettingsToolPanelVisible();
  const showHeaderPanel = smartSearchEnabled || presetsEnabled || globalActionsEnabled;
  const presetState = usePresetState();
  const currentColumnSettings = getPresetStates(props.columnStates);
  const createPresetAdapter = (name: string, desc: string) => presetState.createPreset(name, desc, currentColumnSettings, props.dataGridSettings);
  const updatePresetAdapter = (presetId: string) => presetState.updatePreset(presetId, currentColumnSettings, props.dataGridSettings);
  if (!showHeaderPanel) {
    return null;
  }
  const handleRefreshData = () => {
    props.refreshData();
    refreshDatagrid(props.dataGridProps.gridCode);
  };
  const isPresetActive = isPresetCustom(presetState.activePreset);
  if (isMobile) {
    return <div className="relative" data-testid={suffixTestId('headerWrapper', props)}>
        <div className="flex items-center gap-2 px-2 py-2">
          {smartSearchEnabled && <div className="flex-1 min-w-0">
              <QuickFilterInput data-testid={props['data-testid']} />
            </div>}
          {presetsEnabled && <MobilePresetsButton isPresetActive={isPresetActive} activePanel={props.activePanel} onTogglePanel={props.onTogglePanel} data-testid={props['data-testid']} />}
          {globalActionsEnabled && <HStack justify="flex-end" spacing={1}>
              <IconButton icon="navigation/refresh" onClick={handleRefreshData} />
              {filterToolPanelVisible && <IconButton icon="content/filter_list" onClick={() => props.onTogglePanel(ToolpanelType.filter)} priority={getFilterPriority(props.activePanel, props.isFilterActive)} />}
              {columnToolPanelVisible && <IconButton icon="editor/table_chart" onClick={() => props.onTogglePanel(ToolpanelType.column)} priority={getPanelPriority(props.activePanel, ToolpanelType.column)} />}
              {settingsToolPanelVisible && <IconButton icon="action/settings" onClick={() => props.onTogglePanel(ToolpanelType.settings)} priority={getPanelPriority(props.activePanel, ToolpanelType.settings)} />}
            </HStack>}
        </div>
        <LoadingBar />
      </div>;
  }
  return <div className="relative" data-testid={suffixTestId('headerWrapper', props)}>
      <div className="flex items-center gap-1 px-2 py-2">
        {smartSearchEnabled && <div className="pr-1.5">
            <QuickFilterInput className="w-[240px] flex-shrink-0" data-testid={props['data-testid']} />
          </div>}
        {presetsEnabled && <PresetsPanelRenderer activePreset={presetState.activePreset} isPresetChanged={presetState.isPresetChanged} activatePreset={presetState.activatePreset} deactivatePreset={presetState.deactivatePreset} deletePreset={presetState.deletePreset} duplicatePreset={presetState.duplicatePreset} resetOverrides={presetState.resetOverrides} updatePreset={updatePresetAdapter} createPreset={createPresetAdapter} renamePreset={presetState.renamePreset} updatePresetPosition={presetState.updatePresetPosition} rowCountGetter={presetState.rowCountGetter} presets={presetState.presets} data-testid={props['data-testid']} />}
        {globalActionsEnabled && <HStack grow={1} justify="flex-end" spacing={1}>
            {props.isFilterActive && <Button variant="dangerGhost" leftIcon="content/clear" title={t('page.datagrid.button.resetFilters')} onClick={props.onResetFilters} data-testid={suffixTestId('resetFilters', props)} />}
            <Tooltip label={t('page.datagrid.button.refreshData')}>
              <IconButton aria-label={t('page.datagrid.button.refreshData')} icon="navigation/refresh" onClick={handleRefreshData} data-testid={suffixTestId('refreshDataIconButton', props)} />
            </Tooltip>
            {buildAbsoluteUrl && <SharePresetButton data-testid={props['data-testid']} />}
            <ExportButton data-testid={props['data-testid']} />
            {filterToolPanelVisible && <Tooltip label={t('page.datagrid.labels.filters')}>
                <IconButton icon="content/filter_list" onClick={() => props.onTogglePanel(ToolpanelType.filter)} priority={getFilterPriority(props.activePanel, props.isFilterActive)} data-testid={suffixTestId('toggleFilters', props)} />
              </Tooltip>}
            {columnToolPanelVisible && <Tooltip label={t('page.datagrid.labels.columnSetting')}>
                <IconButton icon="editor/table_chart" onClick={() => props.onTogglePanel(ToolpanelType.column)} priority={getPanelPriority(props.activePanel, ToolpanelType.column)} data-testid={suffixTestId('toggleColumns', props)} />
              </Tooltip>}
            {settingsToolPanelVisible && <Tooltip label={t('page.datagrid.labels.settings')}>
                <IconButton icon="action/settings" onClick={() => props.onTogglePanel(ToolpanelType.settings)} priority={getPanelPriority(props.activePanel, ToolpanelType.settings)} data-testid={suffixTestId('toggleSettings', props)} />
              </Tooltip>}
          </HStack>}
      </div>
      <LoadingBar />
    </div>;
}